import React, { useMemo } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Language } from 'api/Clients/Types/Language'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { SimpleUserResponse, UserStatus } from 'api/Identity/Types/UserResponse'
import { CreateLeadDto } from 'api/Leads/Types/CreateLeadDto'
import { LeadGenderType, LeadType } from 'api/Leads/Types/LeadType'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useCountriesToCountryCodes } from 'hooks/useCountriesToCountryCodes'
import { CircleAlert } from 'lucide-react'
import { stripTimeFromFormat } from 'utils/date'
import { getEnumEntries } from 'utils/getEnumEntries'
import { isChinese } from 'utils/languages'
import { zonedTimeToUtc } from 'utils/time-zone/helpers'

const LockedStatusBadge = () => (
  <Button variant='destructive' size='icon-xs' type='button'>
    <Tooltip>
      <TooltipTrigger>
        <CircleAlert />
      </TooltipTrigger>
      <TooltipContent side='top'>User Locked</TooltipContent>
    </Tooltip>
  </Button>
)

const CreateLeadFormUI: React.FC<FormikProps<CreateLeadFormValues> & OuterProps> = ({
  values,
  isValid,
  leadSources,
  countries,
  languages,
  tickmillCompanies,
  users,
  tickmillProducts,
  dateFormatType,
  isSubmitting,
  setFieldValue,
}) => {
  const uniqueCountryCodes = useCountriesToCountryCodes(countries)

  const countryCodeOptions = useMemo(
    () => uniqueCountryCodes.map((countryCode) => ({ id: countryCode, name: countryCode })),
    [uniqueCountryCodes]
  )

  const genderOptions = useMemo(
    () => getEnumEntries(LeadGenderType).map(([key, value]) => ({ id: value, name: key })),
    []
  )

  const leadTypeOptions = useMemo(
    () => getEnumEntries(LeadType).map(([key, value]) => ({ id: value, name: key })),
    []
  )

  const leadSourceOptions = useMemo(
    () => leadSources.map((source) => ({ id: source.name, name: source.name })),
    [leadSources]
  )

  const assigneeOptions = useMemo(
    () => users.map((user) => ({ id: user.id, name: user.name })),
    [users]
  )

  const selectedAssignee = useMemo(
    () => users.find((user) => user.id === values.assigneeId),
    [users, values.assigneeId]
  )

  const assigneeWarning =
    selectedAssignee?.status?.id === UserStatus.Locked ? <LockedStatusBadge /> : undefined

  return (
    <Form>
      <DataGrid
        rightAddon={
          <Button type='submit' disabled={!isValid || isSubmitting} data-test='create-button'>
            Create
            {isSubmitting && <Spinner data-icon='inline-start' />}
          </Button>
        }
      >
        <DataColumn>
          <DataCard columns={2} title='Main Information'>
            <FormField name='firstName' label='First Name' dataTest='first-name-field' />
            <FormField name='middleName' label='Middle Name' dataTest='middle-name-field' />
            <FormField name='lastName' label='Last Name' dataTest='last-name-field' />
            <FormField name='nativeName' label='Native Name' dataTest='native-name-field' />
            <FormSelectField
              name='genderId'
              label='Gender'
              options={genderOptions}
              dataTest='gender-field'
              onChange={(value) => setFieldValue('genderId', Number(value))}
            />
            <FormSelectField
              name='typeId'
              label='Type'
              options={leadTypeOptions}
              dataTest='type-field'
              onChange={(value) => setFieldValue('typeId', Number(value))}
            />
            <FormDateField
              name='birthDay'
              label='Birthday'
              dateFormatType={stripTimeFromFormat(dateFormatType)}
              dataTest='birth-day-field'
              onChange={(value) => setFieldValue('birthDay', value?.toISOString() || null)}
            />
            <FormSelectField
              name='assigneeId'
              label='CFD Assignee'
              options={assigneeOptions}
              showSearch
              allowClear
              rightAddon={assigneeWarning}
              dataTest='assignee-field'
              onChange={(value) => setFieldValue('assigneeId', value ? String(value) : '')}
            />
          </DataCard>

          <DataCard title='Classification'>
            <FormCheckboxField name='isUsCitizen' label='Is US Citizen' />
            <FormCheckboxField name='optIn' label='Opt In' dataTest='opt-in-field' />
          </DataCard>

          <DataCard columns={2} title='More'>
            <FormSelectField
              name='tickmillCompanyId'
              label='Tickmill Company'
              options={tickmillCompanies}
              showSearch
              dataTest='tickmill-company-field'
              onChange={(value) => setFieldValue('tickmillCompanyId', value ? Number(value) : 0)}
            />
            <FormSelectField
              name='source'
              label='Lead Source'
              options={leadSourceOptions}
              dataTest='source-field'
              onChange={(value) => setFieldValue('source', value ? String(value) : '')}
            />
            <FormTextareaField
              name='sourceDescription'
              label='Lead Source Description'
              rows={3}
              dataTest='source-description-field'
            />
            <FormMultiSelectField
              name='tickmillProducts'
              label='Products'
              options={tickmillProducts}
              value={values.tickmillProducts.map((product) => product.id)}
              showSearch
              allowClear
              dataTest='product-field'
              onChange={(nextValue) => {
                const selectedValues = Array.isArray(nextValue)
                  ? nextValue
                  : nextValue == null
                    ? []
                    : [nextValue]

                setFieldValue(
                  'tickmillProducts',
                  selectedValues
                    .map((selectedValue) =>
                      tickmillProducts.find((product) => product.id === selectedValue)
                    )
                    .filter(Boolean) as NameDto[]
                )
              }}
            />
          </DataCard>
        </DataColumn>

        <DataColumn>
          <DataCard columns={2} title='Contact'>
            <FormSelectField
              name='countryCode'
              label='Country Code'
              options={countryCodeOptions}
              showSearch
              dataTest='country-code-field'
              onChange={(value) => setFieldValue('countryCode', value ? String(value) : '')}
            />
            <FormField name='phoneNumber' label='Phone Number' dataTest='phone-number-field' />
            <FormField name='email' label='Email Address' dataTest='email-field' />
            <FormSelectField
              name='languageId'
              label='Communication Language'
              options={languages}
              showSearch
              dataTest='language-field'
              onChange={(value) => setFieldValue('languageId', value ? String(value) : '')}
            />
          </DataCard>

          <DataCard columns={2} title='Address'>
            <FormField name='address' label='Street' dataTest='address-field' />
            <FormSelectField
              name='countryId'
              label='Country'
              options={countries}
              showSearch
              dataTest='country-field'
              onChange={(value) => setFieldValue('countryId', value ? String(value) : '')}
            />
            <FormField name='city' label='City' dataTest='city-field' />
            <FormField name='state' label='State' dataTest='state-field' />
            <FormField name='postalCode' label='Postal Code' dataTest='postal-code-field' />
          </DataCard>

          <DataCard columns={2} title='Campaign'>
            <FormField name='referrer' label='Referrer' dataTest='referrer-field' />
            <FormField name='referralCode' label='Referral Code' dataTest='referral-code-field' />
            <FormField name='campaign' label='Campaign Name' dataTest='campaign-field' />
            <FormField
              name='campaignSource'
              label='Campaign Source'
              dataTest='campaign-source-field'
            />
            <FormField
              name='campaignMedium'
              label='Campaign Medium'
              dataTest='campaign-medium-field'
            />
            <FormField name='campaignTerm' label='Campaign Term' dataTest='campaign-term-field' />
            <FormTextareaField
              name='campaignContent'
              label='Campaign Content'
              rows={3}
              dataTest='campaign-content-field'
            />
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

interface OuterProps {
  leadSources: NameDto[]
  countries: CountryDto[]
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  users: SimpleUserResponse[]
  tickmillProducts: NameDto[]
  dateFormatType?: string

  onSubmit(values: CreateLeadDto): Promise<void>
}

export interface CreateLeadFormValues {
  email: string
  tickmillCompanyId: number
  phoneNumber: string
  typeId: number
  genderId: number
  firstName: string
  middleName: string
  lastName: string
  nativeName?: string | null
  birthDay: string | null
  countryId: string
  countryCode: string
  address: string
  city: string
  state: string
  postalCode: string
  isUsCitizen: boolean
  languageId: string
  source: string
  sourceDescription: string
  optIn: boolean
  campaign: string
  campaignSource: string
  campaignMedium: string
  campaignTerm: string
  campaignContent: string
  referrer: string
  referralCode: string
  preferredCurrencyId: string
  appendActivityIfExits: boolean
  overrideVerificationCheck: boolean | null
  tickmillPublicDomain: string
  token: string
  assigneeId: string
  tickmillProducts: NameDto[]
}

const dataToSubmit = (values: CreateLeadFormValues): CreateLeadDto => {
  return {
    email: values.email || null,
    tickmillCompanyId: values.tickmillCompanyId,
    phoneNumber: values.phoneNumber || null,
    typeId: values.typeId,
    genderId: values.genderId,
    firstName: values.firstName || null,
    middleName: values.middleName || null,
    lastName: values.lastName || null,
    nativeName: values.nativeName || null,
    birthDay: values.birthDay ? zonedTimeToUtc(new Date(values.birthDay)).toISOString() : null,
    countryId: values.countryId || null,
    countryCode: values.countryCode || null,
    address: values.address || null,
    city: values.city || null,
    state: values.state || null,
    postalCode: values.postalCode || null,
    isUsCitizen: values.isUsCitizen,
    languageId: values.languageId || null,
    source: values.source || null,
    sourceDescription: values.sourceDescription || null,
    optIn: values.optIn,
    campaign: values.campaign || null,
    campaignSource: values.campaignSource || null,
    campaignMedium: values.campaignMedium || null,
    campaignTerm: values.campaignTerm || null,
    campaignContent: values.campaignContent || null,
    referrer: values.referrer || null,
    referralCode: values.referralCode || null,
    preferredCurrencyId: values.preferredCurrencyId || null,
    appendActivityIfExits: values.appendActivityIfExits,
    overrideVerificationCheck: values.overrideVerificationCheck || null,
    tickmillPublicDomain: values.tickmillPublicDomain || null,
    token: values.token || null,
    assigneeId: values.assigneeId || null,
    productIds: values.tickmillProducts.map((product) => product.id),
  }
}

export const CreateLeadForm = withFormik<OuterProps, CreateLeadFormValues>({
  mapPropsToValues: () => {
    return {
      email: '',
      tickmillCompanyId: 0,
      phoneNumber: '',
      typeId: 0,
      genderId: LeadGenderType.Unknown,
      firstName: '',
      middleName: '',
      lastName: '',
      nativeName: '',
      birthDay: null,
      countryId: '',
      countryCode: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      isUsCitizen: false,
      languageId: 'en',
      source: '',
      sourceDescription: '',
      optIn: false,
      campaign: '',
      campaignSource: '',
      campaignMedium: '',
      campaignTerm: '',
      campaignContent: '',
      referrer: '',
      referralCode: '',
      preferredCurrencyId: '',
      appendActivityIfExits: false,
      overrideVerificationCheck: false,
      tickmillPublicDomain: '',
      token: '',
      assigneeId: '',
      tickmillProducts: [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<CreateLeadFormValues> = {}
    if (!values.firstName) {
      errors.firstName = 'Required'
    }
    if (!values.lastName) {
      errors.lastName = 'Required'
    }
    if (values.nativeName && !isChinese(values.nativeName)) {
      errors.nativeName = 'Only chinese characters are allowed'
    }
    if (values.genderId < 0) {
      errors.genderId = 'Required'
    }
    if (!values.countryCode) {
      errors.countryCode = 'Required'
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = 'Required'
    }
    if (!values.email) {
      errors.email = 'Required'
    }
    if (!values.languageId) {
      errors.languageId = 'Required'
    }
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }
    if (!values.countryId) {
      errors.countryId = 'Required'
    }
    if (!values.source) {
      errors.source = 'Required'
    }
    if (!values.typeId) {
      errors.typeId = 'Required'
    }
    if (!values.tickmillProducts.length) {
      errors.tickmillProducts = 'Required'
    }
    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(CreateLeadFormUI)
